'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import AppNav from '@/components/AppNav';
import { PageErrorBoundary, ErrorBoundary } from '@/components/ErrorBoundary';
import { SkeletonCard, SkeletonRow } from '@/components/Skeleton';
import { useToast } from '@/components/Toast';

interface ComplianceTask {
  id: string;
  title: string;
  category: string;
  dueDate: string;
  status: 'pending' | 'in_progress' | 'completed' | 'overdue';
  priority: 'critical' | 'high' | 'medium' | 'low';
  automatable: boolean;
  aiDraftAvailable: boolean;
  description: string;
  penalty?: string;
  frequency: string;
}

interface AutomationRule {
  id: string;
  name: string;
  trigger: string;
  action: string;
  enabled: boolean;
  lastRun?: string;
  nextRun?: string;
}

const demoTasks: ComplianceTask[] = [
  {
    id: '1', title: 'TDS Return Q3 (Form 26Q)', category: 'TDS',
    dueDate: new Date(Date.now() + 15 * 86400000).toISOString(),
    status: 'pending', priority: 'critical', automatable: true, aiDraftAvailable: true,
    description: 'Quarterly TDS return for non-salary payments. Covers July–September.',
    penalty: '₹200/day delay + 1.5% interest/month', frequency: 'Quarterly',
  },
  {
    id: '2', title: 'Director KYC (DIR-3 KYC)', category: 'MCA',
    dueDate: new Date(Date.now() + 45 * 86400000).toISOString(),
    status: 'pending', priority: 'high', automatable: false, aiDraftAvailable: true,
    description: 'Annual KYC for all directors. Required to keep DIN active.',
    penalty: '₹5,000 per director', frequency: 'Annual',
  },
  {
    id: '3', title: 'Annual ROC Filing (MGT-7 + AOC-4)', category: 'MCA',
    dueDate: new Date(Date.now() + 90 * 86400000).toISOString(),
    status: 'in_progress', priority: 'high', automatable: false, aiDraftAvailable: true,
    description: 'Annual return and financial statements filing with MCA.',
    penalty: '₹100/day delay', frequency: 'Annual',
  },
  {
    id: '4', title: 'GST Monthly Return (GSTR-3B)', category: 'GST',
    dueDate: new Date(Date.now() + 8 * 86400000).toISOString(),
    status: 'pending', priority: 'critical', automatable: true, aiDraftAvailable: false,
    description: 'Monthly GST summary return. Due by 20th of following month.',
    penalty: '₹50/day (nil return) or ₹100/day', frequency: 'Monthly',
  },
  {
    id: '5', title: 'Income Tax Advance Tax Q3', category: 'Income Tax',
    dueDate: new Date(Date.now() + 30 * 86400000).toISOString(),
    status: 'pending', priority: 'medium', automatable: false, aiDraftAvailable: false,
    description: '75% of estimated tax liability due by December 15.',
    penalty: '1% interest/month under Section 234C', frequency: 'Quarterly',
  },
  {
    id: '6', title: 'PF & ESI Monthly Contribution', category: 'Labour',
    dueDate: new Date(Date.now() + 5 * 86400000).toISOString(),
    status: 'overdue', priority: 'critical', automatable: true, aiDraftAvailable: false,
    description: 'Monthly PF (12%) and ESI (3.25%) contribution for employees.',
    penalty: '₹5,000 + damages up to 100% of arrears', frequency: 'Monthly',
  },
  {
    id: '7', title: 'Trademark Renewal Check', category: 'Trademark',
    dueDate: new Date(Date.now() + 180 * 86400000).toISOString(),
    status: 'pending', priority: 'medium', automatable: true, aiDraftAvailable: false,
    description: 'Check trademark registration status and renewal requirements.',
    frequency: 'Annual',
  },
];

const demoRules: AutomationRule[] = [
  {
    id: '1', name: 'TDS Reminder', trigger: '30 days before TDS due date',
    action: 'Send email + WhatsApp reminder to CA and founder',
    enabled: true, lastRun: '2026-08-01', nextRun: '2026-09-01',
  },
  {
    id: '2', name: 'GST Auto-Reminder', trigger: '7 days before GSTR-3B due',
    action: 'Send SMS via MSG91 to registered mobile',
    enabled: true, lastRun: '2026-08-13', nextRun: '2026-09-13',
  },
  {
    id: '3', name: 'Director KYC Alert', trigger: '60 days before DIR-3 deadline',
    action: 'Email all directors with KYC checklist',
    enabled: false, nextRun: '2026-09-15',
  },
  {
    id: '4', name: 'PF/ESI Monthly', trigger: '5th of every month',
    action: 'Auto-generate contribution statement and send to HR',
    enabled: true, lastRun: '2026-08-05', nextRun: '2026-09-05',
  },
];

const priorityConfig = {
  critical: { label: 'Critical', color: 'text-danger', bg: 'bg-danger-light', dot: 'bg-danger' },
  high: { label: 'High', color: 'text-warning', bg: 'bg-warning/10', dot: 'bg-warning' },
  medium: { label: 'Medium', color: 'text-ai', bg: 'bg-ai-light', dot: 'bg-ai' },
  low: { label: 'Low', color: 'text-ink-muted', bg: 'bg-surface-3', dot: 'bg-ink-subtle' },
};

const statusConfig = {
  pending: { label: 'Pending', color: 'text-ink-muted', bg: 'bg-surface-3' },
  in_progress: { label: 'In Progress', color: 'text-ai', bg: 'bg-ai-light' },
  completed: { label: 'Completed', color: 'text-success', bg: 'bg-success-light' },
  overdue: { label: 'Overdue', color: 'text-danger', bg: 'bg-danger-light' },
};

export default function ComplianceAutomationPage() {
  const [tasks, setTasks] = useState<ComplianceTask[]>([]);
  const [rules, setRules] = useState<AutomationRule[]>(demoRules);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'tasks' | 'automation' | 'calendar'>('tasks');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');
  const [aiDrafting, setAiDrafting] = useState<string | null>(null);
  const { success, info, error: toastError } = useToast();

  useEffect(() => {
    const timer = setTimeout(() => {
      setTasks(demoTasks);
      setLoading(false);
    }, 1000);
    return () => clearTimeout(timer);
  }, []);

  const getDaysUntil = (dateStr: string) => Math.ceil((new Date(dateStr).getTime() - Date.now()) / 86400000);

  const markComplete = (id: string) => {
    setTasks(prev => prev.map(t => t.id === id ? { ...t, status: 'completed' } : t));
    success('Task marked complete', 'Compliance task updated successfully.');
  };

  const handleAiDraft = async (task: ComplianceTask) => {
    setAiDrafting(task.id);
    try {
      await new Promise(r => setTimeout(r, 1500));
      info('AI Draft Ready', `${task.title} draft prepared. Review before filing.`);
    } catch {
      toastError('AI unavailable', 'Add OPENROUTER_API_KEY to enable AI drafting.');
    } finally {
      setAiDrafting(null);
    }
  };

  const toggleRule = (id: string) => {
    setRules(prev => prev.map(r => r.id === id ? { ...r, enabled: !r.enabled } : r));
    const rule = rules.find(r => r.id === id);
    if (rule) {
      success(rule.enabled ? 'Automation disabled' : 'Automation enabled', rule.name);
    }
  };

  const categories = ['all', ...Array.from(new Set(demoTasks.map(t => t.category)))];
  const filteredTasks = tasks.filter(t => {
    if (filterCategory !== 'all' && t.category !== filterCategory) return false;
    if (filterPriority !== 'all' && t.priority !== filterPriority) return false;
    return true;
  });

  const overdueCount = tasks.filter(t => t.status === 'overdue').length;
  const criticalCount = tasks.filter(t => t.priority === 'critical' && t.status !== 'completed').length;
  const completedCount = tasks.filter(t => t.status === 'completed').length;
  const automationEnabled = rules.filter(r => r.enabled).length;

  return (
    <PageErrorBoundary>
      <div className="min-h-screen bg-surface-2 font-sans">
        <AppNav variant="app" activePath="/compliance/automation" />

        <main className="sm:ml-60 pt-14 sm:pt-0 min-h-screen">
          <div className="max-w-5xl mx-auto px-4 md:px-6 py-6 md:py-8">

            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <Link href="/compliance" className="text-[12px] text-ink-muted hover:text-ink transition-colors">Compliance</Link>
                  <span className="text-ink-subtle">›</span>
                  <span className="text-[12px] text-ink font-medium">Automation Engine</span>
                </div>
                <h1 className="text-2xl font-bold text-ink">Compliance Automation</h1>
                <p className="text-[14px] text-ink-muted mt-0.5">AI-powered compliance tracking with automated reminders and filing assistance.</p>
              </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              {loading ? (
                <>
                  <SkeletonCard />
                  <SkeletonCard />
                  <SkeletonCard />
                  <SkeletonCard />
                </>
              ) : (
                <>
                  <div className="bg-white rounded-xl border border-danger/20 p-4">
                    <p className="text-[11px] font-semibold text-ink-subtle uppercase tracking-wide mb-2">Overdue</p>
                    <p className="text-2xl font-bold text-danger">{overdueCount}</p>
                    <p className="text-[11px] text-ink-muted mt-1">Immediate action</p>
                  </div>
                  <div className="bg-white rounded-xl border border-warning/20 p-4">
                    <p className="text-[11px] font-semibold text-ink-subtle uppercase tracking-wide mb-2">Critical</p>
                    <p className="text-2xl font-bold text-warning">{criticalCount}</p>
                    <p className="text-[11px] text-ink-muted mt-1">High priority</p>
                  </div>
                  <div className="bg-white rounded-xl border border-success/20 p-4">
                    <p className="text-[11px] font-semibold text-ink-subtle uppercase tracking-wide mb-2">Completed</p>
                    <p className="text-2xl font-bold text-success">{completedCount}</p>
                    <p className="text-[11px] text-ink-muted mt-1">This quarter</p>
                  </div>
                  <div className="bg-white rounded-xl border border-ai/20 p-4">
                    <p className="text-[11px] font-semibold text-ink-subtle uppercase tracking-wide mb-2">Automations</p>
                    <p className="text-2xl font-bold text-ai">{automationEnabled}</p>
                    <p className="text-[11px] text-ink-muted mt-1">Active rules</p>
                  </div>
                </>
              )}
            </div>

            {/* Tabs */}
            <div className="flex bg-white border border-border rounded-xl p-1 mb-5 w-fit">
              {([
                { key: 'tasks', label: '📋 Tasks' },
                { key: 'automation', label: '⚡ Automation Rules' },
                { key: 'calendar', label: '📅 Calendar' },
              ] as { key: typeof activeTab; label: string }[]).map(tab => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`px-4 py-2 rounded-lg text-[12px] font-semibold transition-all ${
                    activeTab === tab.key ? 'bg-ai text-white shadow-sm' : 'text-ink-muted hover:text-ink'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Tasks Tab */}
            {activeTab === 'tasks' && (
              <ErrorBoundary>
                <div className="space-y-4">
                  {/* Filters */}
                  <div className="bg-white rounded-xl border border-border p-4">
                    <div className="flex flex-wrap gap-2">
                      <div className="flex gap-1.5">
                        {categories.map(cat => (
                          <button
                            key={cat}
                            onClick={() => setFilterCategory(cat)}
                            className={`px-3 py-1.5 rounded-lg text-[11px] font-semibold transition-all ${filterCategory === cat ? 'bg-ai text-white' : 'bg-surface-2 text-ink-muted hover:text-ink'}`}
                          >
                            {cat === 'all' ? 'All' : cat}
                          </button>
                        ))}
                      </div>
                      <div className="flex gap-1.5 ml-auto">
                        {(['all', 'critical', 'high', 'medium'] as const).map(p => (
                          <button
                            key={p}
                            onClick={() => setFilterPriority(p)}
                            className={`px-3 py-1.5 rounded-lg text-[11px] font-semibold transition-all ${filterPriority === p ? 'bg-ink text-white' : 'bg-surface-2 text-ink-muted hover:text-ink'}`}
                          >
                            {p === 'all' ? 'All Priority' : p.charAt(0).toUpperCase() + p.slice(1)}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>

                  {loading ? (
                    <div className="space-y-3">
                      {[...Array(4)].map((_, i) => <SkeletonRow key={i} />)}
                    </div>
                  ) : filteredTasks.length === 0 ? (
                    <div className="bg-white rounded-xl border border-border p-10 text-center">
                      <p className="text-[15px] font-semibold text-ink mb-2">No tasks found</p>
                      <p className="text-[13px] text-ink-muted">Try adjusting your filters.</p>
                    </div>
                  ) : (
                    filteredTasks.map(task => {
                      const daysLeft = getDaysUntil(task.dueDate);
                      const p = priorityConfig[task.priority];
                      const s = statusConfig[task.status];
                      return (
                        <div key={task.id} className={`bg-white rounded-xl border p-5 transition-all ${task.status === 'overdue' ? 'border-danger/30' : 'border-border'}`}>
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1 min-w-0">
                              <div className="flex flex-wrap items-center gap-2 mb-2">
                                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${p.bg} ${p.color}`}>{p.label}</span>
                                <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${s.bg} ${s.color}`}>{s.label}</span>
                                <span className="text-[10px] bg-surface-3 text-ink-muted px-2 py-0.5 rounded-full">{task.category}</span>
                                <span className="text-[10px] bg-surface-3 text-ink-muted px-2 py-0.5 rounded-full">{task.frequency}</span>
                                {task.automatable && <span className="text-[10px] bg-ai-light text-ai px-2 py-0.5 rounded-full">⚡ Automatable</span>}
                              </div>
                              <p className="text-[14px] font-semibold text-ink">{task.title}</p>
                              <p className="text-[12px] text-ink-muted mt-1">{task.description}</p>
                              {task.penalty && (
                                <p className="text-[11px] text-danger mt-1.5">⚠ Penalty: {task.penalty}</p>
                              )}
                            </div>
                            <div className="text-right flex-shrink-0">
                              <p className={`text-[13px] font-bold ${daysLeft < 0 ? 'text-danger' : daysLeft <= 15 ? 'text-warning' : 'text-ink'}`}>
                                {daysLeft < 0 ? `${Math.abs(daysLeft)}d overdue` : daysLeft === 0 ? 'Due today' : `${daysLeft}d left`}
                              </p>
                              <p className="text-[11px] text-ink-muted">{new Date(task.dueDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}</p>
                            </div>
                          </div>
                          {task.status !== 'completed' && (
                            <div className="flex gap-2 mt-4 pt-3 border-t border-border">
                              {task.aiDraftAvailable && (
                                <button
                                  onClick={() => handleAiDraft(task)}
                                  disabled={aiDrafting === task.id}
                                  className="flex items-center gap-1.5 text-[12px] font-semibold text-ai bg-ai-light px-3 py-1.5 rounded-lg hover:bg-ai hover:text-white transition-all disabled:opacity-50"
                                >
                                  {aiDrafting === task.id ? (
                                    <span className="w-3 h-3 border-2 border-ai/30 border-t-ai rounded-full animate-spin" />
                                  ) : '✨'}
                                  AI Draft
                                </button>
                              )}
                              <button
                                onClick={() => markComplete(task.id)}
                                className="flex items-center gap-1.5 text-[12px] font-semibold text-success bg-success-light px-3 py-1.5 rounded-lg hover:bg-success hover:text-white transition-all"
                              >
                                ✓ Mark Complete
                              </button>
                              <Link
                                href={`/compliance`}
                                className="flex items-center gap-1.5 text-[12px] font-medium text-ink-muted bg-surface-2 px-3 py-1.5 rounded-lg hover:text-ink transition-all"
                              >
                                View Details →
                              </Link>
                            </div>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              </ErrorBoundary>
            )}

            {/* Automation Rules Tab */}
            {activeTab === 'automation' && (
              <div className="space-y-4">
                <div className="bg-ai-light border border-ai/20 rounded-xl p-4 flex items-start gap-3">
                  <span className="text-ai text-lg">⚡</span>
                  <div>
                    <p className="text-[13px] font-semibold text-ai">Automation Engine</p>
                    <p className="text-[12px] text-ink-muted mt-0.5">Rules run automatically based on triggers. Requires MSG91 (SMS), WhatsApp API, and email credentials.</p>
                  </div>
                </div>
                {rules.map(rule => (
                  <div key={rule.id} className={`bg-white rounded-xl border p-5 transition-all ${rule.enabled ? 'border-ai/20' : 'border-border opacity-70'}`}>
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <p className="text-[14px] font-semibold text-ink">{rule.name}</p>
                          <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${rule.enabled ? 'bg-success-light text-success' : 'bg-surface-3 text-ink-muted'}`}>
                            {rule.enabled ? 'Active' : 'Paused'}
                          </span>
                        </div>
                        <div className="space-y-1.5">
                          <div className="flex items-start gap-2">
                            <span className="text-[11px] font-semibold text-ink-muted w-14 flex-shrink-0">Trigger:</span>
                            <span className="text-[12px] text-ink">{rule.trigger}</span>
                          </div>
                          <div className="flex items-start gap-2">
                            <span className="text-[11px] font-semibold text-ink-muted w-14 flex-shrink-0">Action:</span>
                            <span className="text-[12px] text-ink">{rule.action}</span>
                          </div>
                          {rule.lastRun && (
                            <div className="flex items-start gap-2">
                              <span className="text-[11px] font-semibold text-ink-muted w-14 flex-shrink-0">Last run:</span>
                              <span className="text-[12px] text-ink-muted">{rule.lastRun}</span>
                            </div>
                          )}
                          {rule.nextRun && (
                            <div className="flex items-start gap-2">
                              <span className="text-[11px] font-semibold text-ink-muted w-14 flex-shrink-0">Next run:</span>
                              <span className="text-[12px] text-ai font-medium">{rule.nextRun}</span>
                            </div>
                          )}
                        </div>
                      </div>
                      <button
                        onClick={() => toggleRule(rule.id)}
                        className={`relative w-11 h-6 rounded-full transition-all flex-shrink-0 ${rule.enabled ? 'bg-ai' : 'bg-surface-3'}`}
                      >
                        <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-all ${rule.enabled ? 'left-5' : 'left-0.5'}`} />
                      </button>
                    </div>
                  </div>
                ))}
                <div className="bg-surface-2 rounded-xl border border-dashed border-border p-5 text-center">
                  <p className="text-[13px] font-medium text-ink-muted mb-2">Add Custom Automation Rule</p>
                  <p className="text-[12px] text-ink-subtle mb-3">Create custom triggers and actions for your compliance workflow.</p>
                  <button
                    onClick={() => info('Coming soon', 'Custom automation rules will be available in the next release.')}
                    className="text-[12px] font-semibold text-ai bg-ai-light px-4 py-2 rounded-lg hover:bg-ai hover:text-white transition-all"
                  >
                    + Add Rule
                  </button>
                </div>
              </div>
            )}

            {/* Calendar Tab */}
            {activeTab === 'calendar' && (
              <div className="bg-white rounded-xl border border-border p-6">
                <h3 className="text-[14px] font-semibold text-ink mb-5">Upcoming Compliance Deadlines</h3>
                <div className="space-y-2">
                  {[...tasks]
                    .filter(t => t.status !== 'completed')
                    .sort((a, b) => new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime())
                    .map(task => {
                      const daysLeft = getDaysUntil(task.dueDate);
                      const p = priorityConfig[task.priority];
                      return (
                        <div key={task.id} className="flex items-center gap-4 p-3.5 rounded-xl hover:bg-surface-2 transition-all">
                          <div className={`w-2 h-2 rounded-full flex-shrink-0 ${p.dot}`} />
                          <div className="flex-1 min-w-0">
                            <p className="text-[13px] font-medium text-ink truncate">{task.title}</p>
                            <p className="text-[11px] text-ink-muted">{task.category} · {task.frequency}</p>
                          </div>
                          <div className="text-right flex-shrink-0">
                            <p className="text-[12px] font-semibold text-ink">{new Date(task.dueDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
                            <p className={`text-[11px] font-medium ${daysLeft < 0 ? 'text-danger' : daysLeft <= 15 ? 'text-warning' : 'text-ink-muted'}`}>
                              {daysLeft < 0 ? `${Math.abs(daysLeft)}d overdue` : `${daysLeft}d left`}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                </div>
              </div>
            )}
          </div>
        </main>
      </div>
    </PageErrorBoundary>
  );
}
